import { useCallback } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import type {
  BuildEventColumnsArgs,
  MatterAssetEventRow,
  MatterAssetFeatureConfig,
  MatterAssetOpenSource,
  MatterAssetReference,
  MatterAssetSourceRef,
} from '@/matter-asset-common/feature/matter-asset-feature-config'
import { useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import ReferencedTextCell from '@/matter-asset-common/events/ReferencedTextCell'
import MatterRecordSourceFileCell from '@/matter-asset-common/events/MatterRecordSourceFileCell'
import { UITimelineEvent } from '@/timeline/events/store/schemas'
import { selectUITimelineEventsListByJobId } from '@/timeline/events/store/selectors'
import { selectTimelineReferenceById } from '@/timeline/references/store/selectors'
import type { TimelineReference } from '@/timeline/references/store/schemas'
import { useOpenTimelineSource } from '@/timeline/sources/useOpenTimelineSource'
import type { TimelineSourceRef } from '@/timeline/sources/schemas'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import TimelineAssetProcessingSkeleton from './components/TimelineAssetProcessingSkeleton'
import { selectDataErrorForAsset } from './store/selectors'
import { TimelineAssetActions } from './store/slice'

/**
 * Builds the timeline events columns for the shared events table: Date | Description | Source.
 * Reuses the shared referenced-text + source-file cells (med-chron-verified styling); timeline has
 * a single view, so there is no variant filtering. The shared table appends the spacer column
 * itself, so this returns only the data columns.
 */
function buildTimelineEventColumns({ handleScroll }: BuildEventColumnsArgs): ColumnDef<MatterAssetEventRow>[] {
  const columnHelper = createColumnHelper<UITimelineEvent>()

  const columns = [
    columnHelper.display({
      id: 'rowNumber',
      header: '',
      // Show human-friendly row index. Mirrors the med-chron row-number cell (incl. font-medium) so
      // both features read identically in the shared table.
      cell: ({ row }) => <div className="p-1 pt-2 text-xs font-medium text-brand-neutral-600 flex justify-center">{row.index + 1}</div>,
      size: 35,
    }),
    columnHelper.accessor('eventDate', {
      header: 'Date',
      cell: ({ getValue, row, cell }) => <ReferencedTextCell item={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />,
      size: 134,
      meta: { hoverable: true },
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 578,
      meta: { hoverable: true },
    }),
    columnHelper.accessor('source', {
      header: 'Source',
      cell: ({ getValue, row, cell }) => (
        <MatterRecordSourceFileCell
          source={getValue()}
          pages={row.original.pages}
          cellPosition={{ rowId: row.id, cellId: cell.id }}
          scrollToCell={handleScroll}
        />
      ),
      size: 297,
    }),
  ]

  // The shared table works with a minimal row type; the columns above are typed against the full
  // UITimelineEvent row they actually receive at runtime. Erase the row type at this boundary.
  return columns as unknown as ColumnDef<MatterAssetEventRow>[]
}

/**
 * Opens timeline source files and references in the content viewer. Reference opens carry the
 * reference id + highlights and fire the reference-viewer-opened analytics; file-pill/page-chip
 * opens are file-level (no reference analytics). The shared cells narrow back through these.
 */
function useTimelineOpenSource(): MatterAssetOpenSource {
  const storeKey = useMatterAssetStoreKey()
  const { trackEvent } = useAnalytics()
  const { openTimelineSource, sourceLoadError } = useOpenTimelineSource()

  const openReferenceSource = useCallback(
    async (reference: MatterAssetReference) => {
      // The shared layer holds references as a minimal shape; here we work with the full one.
      const ref = reference as unknown as TimelineReference
      const opened = await openTimelineSource(ref.source, {
        referenceId: ref.id,
        highlights: ref.highlights,
        startingPage: ref.highlights.length === 0 ? ref.pages?.[0] : undefined,
      })

      // Track reference viewer opened (only on a successful open)
      if (opened && storeKey) {
        trackEvent(AnalyticsEvent.TimelineAssetReferenceViewerOpened, {
          timelineAssetId: storeKey,
          timelineReferenceId: ref.id,
        })
      }

      return opened
    },
    [openTimelineSource, storeKey, trackEvent]
  )

  const openSourceFile = useCallback(
    (source: MatterAssetSourceRef, options?: { startingPage?: number }) => openTimelineSource(source as TimelineSourceRef, options),
    [openTimelineSource]
  )

  return { openReferenceSource, openSourceFile, sourceLoadError }
}

/**
 * Feature config that drives the shared matter-asset components for the living Timeline.
 * Reproduces the timeline data wiring, columns, copy, references, and retry behavior, reusing the
 * med-chron-verified shared components. Mirrors the shape of medChronFeatureConfig.
 */
export const timelineFeatureConfig: MatterAssetFeatureConfig = {
  key: 'timeline',
  selectUIEventsByStoreKey: (state, { storeKey }) => selectUITimelineEventsListByJobId(state, { timelineJobId: storeKey }),
  buildEventColumns: buildTimelineEventColumns,
  EventsLoadingSkeleton: TimelineAssetProcessingSkeleton,
  selectDataError: (state, { storeKey }) => selectDataErrorForAsset(state, { assetId: storeKey }),
  onRetryLoadEvents: (dispatch, { storeKey }) => dispatch(TimelineAssetActions.refreshAssetData({ assetId: storeKey })),
  copy: {
    eventsErrorTitle: 'Error Loading Timeline',
    emptyEventsMessage: 'No events to display',
  },
  selectReferenceById: (state, { storeKey, referenceId }) => selectTimelineReferenceById(state, { timelineJobId: storeKey, referenceId }),
  selectIsReferenceActive: (state, { referenceId }) =>
    selectActiveContentViewerContextMatches(state, { kind: ViewerOpenContextKind.TIMELINE_REFERENCE, referenceId }),
  useOpenSource: useTimelineOpenSource,
}
